export default {
  name: 'dashify:Gauge',
  title: 'Gauge',
  owner: 'System',
  solution: '',
  tags: ['data', 'visualization', 'gauge'],
  createdAt: 'May 30, 2023, 10:14 AM',
  updatedAt: 'May 31, 2023, 11:14 AM',
  spec: {
    globals: {
      timerange: {
        since: 'now-1h',
        until: 'now',
      },
    },
    props: {
      value: '',
      metricTitle: '',
      minValue: 0,
      maxValue: 100,
      baselineValue: null,
      targetValue: null,
      showThresholds: false,
      thresholds: [],
      unit: '',
    },
    since: '/${ globals.variables.since }',
    until: '/${ globals.variables.until }',
    status: '/${ $toGaugeStatus(props.thresholds, props.value? props.value[0] : null) }',
    targetValueTickWidth: '/${ (props.maxValue - props.minValue) / 100 }',
    plotBandsWithThresholds: [
      {
        color: 'var(--color-positive-bg)', // Color value
        from: '/${ props.thresholds[0] }', // Start of the plot band
        to: '/${ props.thresholds[1] }',
        zIndex: 4,
        outerRadius: '100%',
        innerRadius: '93%',
      },
      {
        color: 'var(--color-warning-bg)', // Color value
        from: '/${ props.thresholds[1] }', // Start of the plot band
        to: '/${ props.thresholds[2] }',
        zIndex: 4,
        outerRadius: '100%',
        innerRadius: '93%',
      },
      {
        color: 'var(--color-severe-warning-bg)', // Color value
        from: '/${ props.thresholds[2] }', // Start of the plot band
        to: '/${ props.maxValue }',
        zIndex: 4,
        outerRadius: '100%',
        innerRadius: '93%',
      },
      {
        //main band fill
        from: '/${ props.minValue }',
        to: '/${ props.value? props.value[0] : 0 }',
        zIndex: 3,
        color: '/${ $toGaugeFillColor(status) }',
        innerRadius: '70%',
        outerRadius: '90%',
        thickness: '15%',
      },
      {
        //main band background
        from: '/${ props.minValue }',
        to: '/${ props.maxValue }',
        zIndex: 1,
        color: 'var(--color-default-bg-strong-disabled)',
        innerRadius: '70%',
        outerRadius: '90%',
        thickness: '15%',
      },
    ],
    plotBandsWithoutThresholds: [
      {
        //main band arc
        from: '/${ props.minValue }',
        to: '/${ props.maxValue }',
        zIndex: 3,
        color: 'var(--color-default-bg-strong-disabled)',
        innerRadius: '90%',
        thickness: '10%',
      },
      {
        //main band fill
        from: '/${ props.minValue }',
        to: '/${ props.value? props.value[0] : 0 }',
        zIndex: 4,
        color: '/${ props.targetValue ? "var(--color-accent-d)" : $toGaugeFillColor(status) }',
        innerRadius: '90%',
        thickness: '10%',
      },
      {
        //target
        from: '/${ props.targetValue ? props.targetValue - targetValueTickWidth / 2 : 0 }',
        to: '/${ props.targetValue ? props.targetValue + targetValueTickWidth / 2 : 0 }',
        zIndex: 5,
        color: "/${ props.targetValue ? 'var(--color-default-text)' : 'transparent' }",
        outerRadius: '110%',
        thickness: '10%',
      },
    ],

    defaults: {
      chart: {
        type: 'solidgauge',
        styledMode: false,
        innerRadius: '70%',
        outerRadius: '100%',
        plotBackgroundColor: 'transparent',
        borderColor: null,
        margin: [0, 0, 0, 0],
        spacing: [0, 0, 0, 0],
      },

      pane: {
        center: ['50%', '75%'],
        size: '125%',
        startAngle: -90,
        endAngle: 90,
        background: {
          backgroundColor: null,
          borderColor: null,
          innerRadius: '60%', //baseline radius
          outerRadius: '100%', //baseline radius
          shape: 'arc',
        },
      },

      tooltip: {
        enabled: true,
        useHTML: true,
        outside: true,
        backgroundColor: 'var(--color-default-bg)',
        style: {
          fontFamily: 'var(--typography-font-body)',
          fontSize: '11px',
          fontStyle: 'normal',
          fontWeight: '400',
          color: 'var(--color-default-text)',
          backgroundColor: 'var(--color-default-bg)',
        },
        zIndex: 5,
        format:
          '/${ $toGaugeTooltip(props.thresholds, props.metricTitle, props.value? $toNumberShort(props.value[0]) : null, props.maxValue, props.unit, props.baselineValue, props.targetValue, props.showThresholds) }',
        followPointer: true,
      },

      yAxis: {
        title: {
          text: '/${ props.metricTitle }', // 'Gauge' title is set from wwmcuis.dataMetricName.
          y: 80, // y is set to be 1/2 the widget height adjusted for text height (-70 orig).
        },
        min: '/${ props.minValue }',
        max: '/${ props.maxValue }',
        tickPositions: ['/${ props.minValue }', '/${ props.maxValue }'], //show min and max values
        tickLength: 0,
        tickWidth: 0,
        labels: {
          //min and max labels
          enabled: true,
          distance: -5,
          y: 20,
          style: {
            fontFamily: 'var(--typography-font-body)',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '400',
            color: 'var(--color-default-text-medium)',
            backgroundColor: 'var(--color-default-bg)',
          },
        },
        minorTicks: false,
        lineWidth: 0,
        tickAmount: 1,
        plotLines: [
          {
            value: '/${ props.baselineValue }', // The value at which you want the baseline to be placed
            color: 'var(--color-default-bg-strong-active)', // The color of the baseline
            width: 2, // The width of the baseline line
            zIndex: 5, // The z-index to control the display order (optional)
            dashStyle: 'ShortDot',
          },
        ],
        plotBands:
          '/${ $count(props.thresholds) > 0 and props.showThresholds ? plotBandsWithThresholds : plotBandsWithoutThresholds }',
      },

      plotOptions: {
        solidgauge: {
          innerRadius: '100%',
          dataLabels: {
            y: 40,
            borderWidth: 0,
            useHTML: false,
          },
        },
      },

      series: [
        {
          zIndex: 4,
          name: '/${ props.metricTitle }',
          data: '/${ props.value? props.value[0] : null }',
          min: '/${ props.minValue }',
          max: '/${ props.maxValue }',
          dataLabels: {
            y: -2,
            align: 'center',
            verticalAlign: 'middle',
            useHTML: true,
            style: {
              textOutline: 'none',
              inside: true,
              fontFamily: 'var(--typography-font-body)',
              fontSize: '11px',
              fontStyle: 'normal',
              fontWeight: '400',
              color: 'var(--color-default-text-strong)',
              backgroundColor: 'var(--color-default-bg)',
            },
            format:
              '/${ $toGaugeTitle(props.unit, "", props.value? $toNumberShort(props.value[0]) : null)}',
          },
        },
      ],
    },
    highchartsConfig: "/${ $omit(props, ['dbPresets']) }",
    view: [['dashify:Highcharts', '/${ $defaultsDeep({}, legendConfig, highchartsConfig, defaults) }']],
  },
};
